import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileBarChart, Download } from "lucide-react";
import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { Button } from "@/components/ui/button";

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: Function;
    lastAutoTable: {
      finalY: number;
    };
  }
}

interface Department {
  id: string;
  name: string;
  sigla: string;
}

interface Supplier {
  id: number;
  name: string;
}

interface Transaction {
  id: number;
  product_id: number;
  type: string;
  quantity: number;
  created_at: string;
}

interface Product {
  product_id: number;
  product_name: string;
  quantity: number;
  min_quantity: number;
  department_name: string;
  status: string;
  supplier_name: string;
}

interface InventoryItem {
  id: string;
  quantity: number;
  initial_quantity: number;
  products: {
    id: string;
    name: string;
    min_quantity: number;
  };
}

const Reports = () => {
  const { toast } = useToast();
  const [timeFrame, setTimeFrame] = useState("monthly");
  const [chartType, setChartType] = useState("line");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");
  const [selectedSupplier, setSelectedSupplier] = useState<string>("all");
  const [departments, setDepartments] = useState<Department[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [topProducts, setTopProducts] = useState<{name: string, value: number}[]>([]);
  const [lowStockProducts, setLowStockProducts] = useState<Product[]>([]);

  useEffect(() => {
    fetchDepartments();
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (selectedDepartment) {
      fetchTransactions();
      fetchTopProducts();
      fetchLowStockProducts();
    }
  }, [selectedDepartment, selectedSupplier, timeFrame]);

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('id, name, sigla')
        .order('sigla', { ascending: true });

      if (error) throw error;
      setDepartments(data || []);
      
      if (data && data.length === 1) {
        setSelectedDepartment(data[0].id);
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar as secretarias",
        variant: "destructive",
      });
    }
  };

  const fetchSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .order('name', { ascending: true });

      if (error) throw error;
      setSuppliers(data || []);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar os fornecedores",
        variant: "destructive",
      });
    }
  };

  const fetchTransactions = async () => {
    try {
      const startDate = new Date();
      if (timeFrame === "weekly") {
        startDate.setDate(startDate.getDate() - 7);
      } else {
        startDate.setMonth(startDate.getMonth() - 6);
      }

      const { data, error } = await supabase
        .from('transactions')
        .select(`
          id,
          product_id,
          type,
          quantity,
          created_at
        `)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;
      setTransactions(data || []);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar as transações",
        variant: "destructive",
      });
    }
  };

  const fetchTopProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('product_inventory')
        .select('*')
        .eq('department_id', selectedDepartment)
        .order('quantity', { ascending: false })
        .limit(5);

      if (error) throw error;

      setTopProducts(
        (data || []).map(product => ({
          name: product.product_name,
          value: product.quantity
        }))
      );
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar os produtos mais movimentados",
        variant: "destructive",
      });
    }
  };

  const fetchLowStockProducts = async () => {
    try {
      // Primeiro, buscar produtos com seus fornecedores
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`
          id,
          name,
          min_quantity,
          supplier_id,
          suppliers (id, name)
        `);

      if (productsError) throw productsError;

      // Depois, buscar o inventário
      const { data: inventoryData, error } = await supabase
        .from('inventory')
        .select(`
          id,
          quantity,
          initial_quantity,
          product_id
        `)
        .order('quantity', { ascending: true });

      if (error) throw error;

      if (!inventoryData || !productsData) {
        setLowStockProducts([]);
        return;
      }

      // Combinar os dados
      const combinedData = inventoryData.map(inv => {
        const product = productsData.find(p => p.id === inv.product_id);
        return {
          inventory: inv,
          product: product || { id: inv.product_id, name: 'Produto desconhecido', min_quantity: 0 },
          supplier: product?.suppliers || null
        };
      });

      // Filtrar por fornecedor se necessário
      let filteredData = combinedData;
      if (selectedSupplier && selectedSupplier !== 'all') {
        filteredData = combinedData.filter(item => {
          // Verificar se o produto tem a propriedade supplier_id
          const productWithSupplier = item.product as { id: any; name: any; min_quantity: any; supplier_id: any; suppliers: { id: any; name: any; }[] };
          return productWithSupplier.supplier_id === parseInt(selectedSupplier);
        });
      }

      // Filtrar produtos com estoque baixo
      const lowStock: Product[] = filteredData
        .filter(item => {
          const currentStock = item.inventory.quantity;
          const minQuantity = item.product.min_quantity || Math.ceil(item.inventory.initial_quantity * 0.2);
          return currentStock <= minQuantity;
        })
        .map(item => ({
          product_id: Number(item.product.id),
          product_name: item.product.name,
          quantity: item.inventory.quantity,
          min_quantity: item.product.min_quantity || Math.ceil(item.inventory.initial_quantity * 0.2),
          department_name: 'N/A',
          status: 'Estoque Baixo',
          supplier_name: item.supplier && Array.isArray(item.supplier) && item.supplier.length > 0 ? item.supplier[0].name : 'Não informado'
        }));

      setLowStockProducts(lowStock);
    } catch (error: any) {
      console.error('Erro ao carregar produtos com estoque baixo:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os produtos com estoque baixo",
        variant: "destructive",
      });
    }
  };

  const getChartData = () => {
    if (!transactions.length) return [];

    const groupedData = new Map();
    
    transactions.forEach(transaction => {
      const date = new Date(transaction.created_at);
      const key = timeFrame === "weekly" 
        ? date.toLocaleDateString('pt-BR', { weekday: 'short' })
        : date.toLocaleDateString('pt-BR', { month: 'short' });
      
      const currentValue = groupedData.get(key) || { entradas: 0, saidas: 0 };
      
      if (transaction.type === 'entrada') {
        currentValue.entradas += transaction.quantity;
      } else {
        currentValue.saidas += transaction.quantity;
      }
      
      groupedData.set(key, currentValue);
    });

    return Array.from(groupedData.entries()).map(([name, value]) => ({
      name,
      ...value
    }));
  };

  const generatePDF = async () => {
    try {
      const doc = new jsPDF();
      const chartElement = document.querySelector('#stockChart');
      
      if (!chartElement) {
        throw new Error('Chart element not found');
      }

      const canvas = await html2canvas(chartElement as HTMLElement);
      const chartImage = canvas.toDataURL('image/png');

      // Título
      doc.setFontSize(18);
      doc.text('Relatório de Estoque', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
      
      // Informações do departamento e período
      doc.setFontSize(12);
      const departmentName = departments.find(d => d.id === selectedDepartment)?.name || 'Todos';
      doc.text(`Departamento: ${departmentName}`, 20, 25);
      doc.text(`Período: ${timeFrame === 'monthly' ? 'Mensal' : 'Anual'}`, 20, 32);
      
      // Adicionar gráfico
      doc.addImage(chartImage, 'PNG', 15, 40, 180, 80);
      
      // Tabela de produtos com estoque baixo
      doc.text('Produtos com Estoque Baixo', 20, 130);
      (doc as any).autoTable({
        head: [['Produto', 'Fornecedor', 'Quantidade', 'Quantidade Mínima', 'Status']],
        body: lowStockProducts.map(product => [
          product.product_name,
          product.supplier_name,
          product.quantity,
          product.min_quantity,
          product.status
        ]),
        startY: 135,
        theme: 'grid',
        margin: { top: 10 }
      });
      
      // Tabela de produtos mais movimentados
      const topMovedY = (doc as any).lastAutoTable.finalY + 10;
      doc.text('Produtos Mais Movimentados', 20, topMovedY);
      (doc as any).autoTable({
        head: [['Produto', 'Quantidade']],
        body: topProducts.map(product => [
          product.name,
          product.value
        ]),
        startY: topMovedY + 5,
        theme: 'grid',
        margin: { top: 10 }
      });
      
      // Data de geração
      const date = new Date().toLocaleDateString('pt-BR');
      doc.setFontSize(10);
      doc.text(`Gerado em: ${date}`, 20, doc.internal.pageSize.getHeight() - 10);
      
      doc.save('relatorio-estoque.pdf');
      
      toast({
        title: "Sucesso",
        description: "Relatório gerado com sucesso",
      });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o relatório",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <FileBarChart className="h-8 w-8" />
          <h1 className="text-3xl font-bold">Relatórios</h1>
        </div>
        <Button onClick={generatePDF} className="gap-2">
          <Download className="h-4 w-4" />
          Baixar PDF
        </Button>
      </div>

      <div className="flex flex-col gap-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Secretaria</label>
            <Select
              value={selectedDepartment}
              onValueChange={setSelectedDepartment}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione uma secretaria" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.sigla} - {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Fornecedor</label>
            <Select
              value={selectedSupplier}
              onValueChange={setSelectedSupplier}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione um fornecedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os fornecedores</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id.toString()}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Período</label>
              <Select value={timeFrame} onValueChange={setTimeFrame}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="monthly">Mensal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Tipo de Gráfico</label>
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="line">Linha</SelectItem>
                  <SelectItem value="bar">Barra</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {selectedDepartment ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Movimentação de Estoque</CardTitle>
                <CardDescription>
                  {timeFrame === "monthly" ? "Últimos 6 meses" : "Última semana"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]" id="stockChart">
                  <ResponsiveContainer width="100%" height="100%">
                    {chartType === "line" ? (
                      <LineChart data={getChartData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          name="Entradas"
                          dataKey="entradas"
                          stroke="#22c55e"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          name="Saídas"
                          dataKey="saidas"
                          stroke="#ef4444"
                          strokeWidth={2}
                        />
                      </LineChart>
                    ) : (
                      <BarChart data={getChartData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar name="Entradas" dataKey="entradas" fill="#22c55e" />
                        <Bar name="Saídas" dataKey="saidas" fill="#ef4444" />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Produtos com Maior Estoque</CardTitle>
                <CardDescription>
                  Top 5 produtos com maior quantidade em estoque
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topProducts}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar name="Quantidade" dataKey="value" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="space-y-1">
                <CardTitle className="text-base sm:text-lg">
                  Produtos com Estoque Baixo
                </CardTitle>
                <CardDescription>
                  Produtos que precisam de reposição
                </CardDescription>
              </div>
              <FileBarChart className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {lowStockProducts.map((product) => (
                  <div
                    key={product.product_id}
                    className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0"
                  >
                    <div>
                      <p className="font-medium text-sm sm:text-base">
                        {product.product_name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">
                          {product.quantity} unid.
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Mínimo: {product.min_quantity} unid.
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {lowStockProducts.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                    <FileBarChart className="h-8 w-8 mb-2 opacity-50" />
                    <p>Nenhum produto com estoque baixo</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          Selecione uma secretaria para ver os relatórios
        </div>
      )}
    </div>
  );
};

export default Reports;